/**
 * Единый форматтер денег (DEV-301, ADR-0006).
 *
 * Backend отдаёт цены типом vinprop.common.v1.Money (`V1Money` в сгенерированных
 * клиентах): amountMinor в минорных единицах валюты + currency. Деление на
 * основную единицу зависит от валюты: USD/RUB — 100, VND — 1 (донг сам себе
 * минорная единица). Никаких «/100» вне этого модуля быть не должно.
 */

/** Структурный аналог V1Money из любого сгенерированного клиента. */
export interface Money {
  amountMinor?: number | string
  /** 'CURRENCY_USD' | 'CURRENCY_VND' | 'CURRENCY_RUB' (или короткий код 'USD'…) */
  currency?: string
}

export type CurrencyCode = 'USD' | 'VND' | 'RUB'

interface CurrencyMeta {
  symbol: string
  /** 10^minorUnit минорных единиц в основной */
  minorUnit: number
  /** Нативная локаль разделителей: «10.000.000 ₫», «1 000 ₽», «$1,000» */
  locale: string
}

const CURRENCY_META: Record<CurrencyCode, CurrencyMeta> = {
  USD: { symbol: '$', minorUnit: 2, locale: 'en-US' },
  VND: { symbol: '₫', minorUnit: 0, locale: 'vi-VN' },
  RUB: { symbol: '₽', minorUnit: 2, locale: 'ru-RU' },
}

const DEFAULT_CURRENCY: CurrencyCode = 'USD'

export interface MoneyFormatOptions {
  /** Показывать символ валюты (по умолчанию true) */
  showSymbol?: boolean
  /** Показывать десятичные знаки основной единицы (по умолчанию false) */
  showDecimals?: boolean
  /** Префикс «от » (по умолчанию false) */
  showFrom?: boolean
  /** Текст для пустой/нулевой суммы (по умолчанию '—') */
  emptyText?: string
}

/**
 * Привести значение валюты из API к короткому коду.
 * Принимает enum-имя ('CURRENCY_USD') или короткий код ('usd').
 * Неизвестное/пустое значение трактуется как USD (исторический инвариант).
 */
export function normalizeCurrencyCode(raw?: string): CurrencyCode {
  const code = (raw ?? '').replace(/^CURRENCY_/, '').toUpperCase()
  return (code in CURRENCY_META ? code : DEFAULT_CURRENCY) as CurrencyCode
}

/** Символ валюты для UI (подписи полей, селекты). */
export function getCurrencySymbol(currency?: string): string {
  return CURRENCY_META[normalizeCurrencyCode(currency)].symbol
}

/**
 * Обернуть legacy-значение «центы USD» в Money.
 * Только для deprecated int64-полей API и локальных USD-сумм — новые места
 * должны брать готовые Money-поля (priceFromMoney/totalPriceMoney/moneyFrom…).
 */
export function moneyFromCents(cents: number | string | undefined | null): Money | null {
  if (cents === undefined || cents === null || cents === '') {
    return null
  }
  return { amountMinor: cents, currency: 'CURRENCY_USD' }
}

/** Money из суммы в ОСНОВНЫХ единицах валюты (ввод пользователя). */
export function moneyFromMajor(major: number, currency: CurrencyCode): Money {
  const { minorUnit } = CURRENCY_META[currency]
  return {
    amountMinor: Math.round(major * 10 ** minorUnit),
    currency: `CURRENCY_${currency}`,
  }
}

/** Сумма Money в основных единицах валюты. */
export function moneyToMajor(money: Money | null | undefined): number {
  if (!money) return 0
  const raw = money.amountMinor
  const minor = typeof raw === 'string' ? parseInt(raw, 10) : (raw ?? 0)
  if (isNaN(minor)) return 0
  const { minorUnit } = CURRENCY_META[normalizeCurrencyCode(money.currency)]
  return minor / 10 ** minorUnit
}

/** Положительная ли сумма. */
export function isPositiveMoney(money: Money | null | undefined): boolean {
  return moneyToMajor(money) > 0
}

/**
 * Единый форматтер цены.
 *
 * @example
 * formatMoney({ amountMinor: '24479100', currency: 'CURRENCY_USD' }) // "$244,791"
 * formatMoney({ amountMinor: 430000000, currency: 'CURRENCY_VND' }) // "₫430.000.000"
 * formatMoney(moneyFromCents(150000)) // "$1,500"
 */
export function formatMoney(
  money: Money | null | undefined,
  options: MoneyFormatOptions = {},
): string {
  const { showSymbol = true, showDecimals = false, showFrom = false, emptyText = '—' } = options

  if (!isPositiveMoney(money) && moneyToMajor(money) === 0) {
    return emptyText
  }

  const code = normalizeCurrencyCode(money?.currency)
  const meta = CURRENCY_META[code]
  const decimals = showDecimals ? 2 : 0

  const formatted = new Intl.NumberFormat(meta.locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(moneyToMajor(money))

  let result = showSymbol ? `${meta.symbol}${formatted}` : formatted
  if (showFrom) {
    result = `от ${result}`
  }

  return result
}

/** UI-локаль → BCP-47 локаль для компактной нотации (млрд/млн ↔ B/M ↔ tỷ/triệu). */
const COMPACT_LOCALE: Record<string, string> = {
  ru: 'ru-RU',
  en: 'en-US',
  vn: 'vi-VN',
}

/**
 * Компактный форматтер цены для карточек каталога: «₫ 2,4 млрд», «₫ 850 млн».
 * Аббревиатура следует UI-локали (не валютной): ru→млрд/млн, en→B/M, vn→tỷ/triệu.
 * Для нулевой/пустой суммы возвращает `zeroText` (по умолчанию '' — вызывающий
 * прячет блок цены через v-if). Передай zeroText (напр. '0 ₫'), если нулевую
 * сумму нужно показать явно (KPI отчётов), а не скрыть.
 *
 * @example
 * formatMoneyShort({ amountMinor: '2400000000', currency: 'CURRENCY_VND' }, { locale: 'ru' }) // "₫ 2,4 млрд"
 * formatMoneyShort({ currency: 'CURRENCY_VND' }, { zeroText: '0 ₫' }) // "0 ₫"
 */
export function formatMoneyShort(
  money: Money | null | undefined,
  options: { locale?: string; zeroText?: string } = {},
): string {
  if (!isPositiveMoney(money)) return options.zeroText ?? ''

  const meta = CURRENCY_META[normalizeCurrencyCode(money?.currency)]
  const locale = COMPACT_LOCALE[options.locale ?? 'ru'] ?? 'ru-RU'
  const compact = new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(moneyToMajor(money))

  return `${meta.symbol} ${compact}`
}

/**
 * Привести Money к виду сгенерированного V1Money для отправки в API:
 * amountMinor — строкой (int64), currency — enum-именем.
 */
export function toApiMoney(
  money: Money | null | undefined,
): { amountMinor: string; currency: string } | undefined {
  if (!money || money.amountMinor === undefined) return undefined
  return {
    amountMinor: String(money.amountMinor),
    currency: `CURRENCY_${normalizeCurrencyCode(money.currency)}`,
  }
}
